<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="[
      `main-product--${containerContext}`,
    ]"
  >
    <div class="grid no-row-gap">
      <div class="main-product__grid-left col xs4 l6">
        <product-gallery
          :container-context="containerContext"
          :namespace="namespace"
          :product="product"
          :variant="variant"
          :variant-image-data="variantImageData"
          :variant-status-badge-data="variantStatusBadgeData"
          :variant-model-data="variantModelData"
        />
      </div>

      <div class="main-product__grid-right col xs4 l8-11">
        <div class="main-product__breadcrumbs-container">
          <breadcrumbs
            :ancestor-title="liquid.collection.title"
            :ancestor-url="liquid.collection.url"
            :backup-url="liquid.backupCollection.url"
            :backup-title="liquid.backupCollection.title"
            :state="state"
            :title="product.title || liquid.title"
          />
        </div>

        <h1
          :id="`section-title-${liquid.sectionId}`"
          class="main-product__title"
          v-text="product.title || liquid.title"
        />

        <div class="main-product__prices-container">
          <product-prices
            :available="product?.available"
            block-class="main-product"
            :compare-at-price="compareAtPrice"
            :price="price || liquid.price"
          />

          <div
            v-if="liquid.vatMessageEnable || liquid.shippingMessageEnable"
            class="main-product__vat-shipping-container text-body-m"
          >
            <p>
              <!-- Display VAT message -->
              <span
                v-if="liquid.vatMessageEnable && marketSettings.marketTax != null"
                class="main-product__vat-message"
                v-text="
                  marketSettings.marketTax
                    ? $string('product.market.tax_included')
                    : $string('product.market.tax_excluded')
                "
              />

              <!-- Display shipping message -->
              <span
                v-if="
                  liquid.shippingMessageEnable &&
                    (
                      !marketSettings.freeShippingThreshold ||
                      marketSettings.freeShippingThreshold > price
                    )
                "
                class="main-product__shipping-message"
                v-html="$string('product.market.shipping_message_html')"
              />
            </p>
          </div>
        </div>

        <div class="main-product__description-container">
          <product-description
            :liquid="liquid"
            :product="product"
            :variant="variant"
          />
        </div>

        <div class="main-product__form">
          <product-form
            :addon-teaser-data="addonTeaserData"
            :addons="addons"
            :blocked-variants-list="blockedVariantsList"
            :out-of-stock-variants-list="outOfStockVariantsList"
            :hidden-variants-list="hiddenVariantsList"
            :disable-purchasing="disablePurchasing"
            :liquid="liquid"
            :model-teaser-data="modelTeaserData"
            :price="price"
            :product="product"
            :sticky-position="stickyPosition"
            :variant="variant"
            @clear-addons="clearAddons"
            @update-addon="updateAddOn"
            @update-variant="handleVariantUpdate"
          />

          <klarna-placement
            v-if="klarnaIsEnabled"
            id="klarna-credit-promotion"
            class="main-product__klarna"
            data-key="credit-promotion-badge"
            :data-locale="klarnaLocale"
            :data-purchase-amount="price || liquid.price"
          />

          <div
            id="alma-widget"
            class="alma-widget"
          />
        </div>

        <div class="main-product__usp-container">
          <product-usp
            :product="product"
            :variant="variant"
            :variant-usp-data="variantUspData"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {Boolean} liquid.vatMessageEnable - Theme settings to enable Vat
 * message.
 * @param {Boolean} liquid.shippingMessageEnable - Theme settings to enable
 * Shipping message.
 * @param {Boolean} liquid.skuEnable - Theme settings to enable Sku.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} modelTeaserData - Metaobject model teaser data.
 * @param {Object} addonTeaserData - Metaobject addon teaser data.
 *
 */
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs'
import ProductDescription from '~async/product-description/product-description'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import ProductUsp from '~async/product-usp/product-usp'

import product from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'

export default {
  name: 'MainProduct',

  components: {
    Breadcrumbs,
    ProductDescription,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ProductUsp,
  },

  props: {
    containerContext: {
      type: String,
      default: 'pdp',
    },
    handle: {
      type: [Boolean, String],
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        hasOnlyDefaultVariant: false,
        price: 0,
        sectionId: '',
        title: '',
        addOnProduct: false,
        vatMessageEnable: false,
        shippingMessageEnable: false,
        skuEnable: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    variantImageData: {
      type: [Boolean, Object],
      default: false,
    },
    variantModelData: {
      type: [Boolean, Object],
      default: false,
    },
    variantStatusBadgeData: {
      type: [Boolean, Object],
      default: false,
    },
    variantUspData: {
      type: [Boolean, Object],
      default: false,
    },
    stickyPosition: {
      type: String,
      default: 'top',
    },
    marketSettings: {
      type: Object,
      default: () => ({
        marketTax: false,
        freeShippingThreshold: 0,
      }),
    },
    modelTeaserData: {
      type: Object,
      default: () => ({}),
    },
    addonTeaserData: {
      type: Object,
      default: () => ({}),
    },
    blockedVariantsList: {
      type: Array,
      default: () => [],
    },
    outOfStockVariantsList: {
      type: Array,
      default: () => [],
    },
    hiddenVariantsList: {
      type: Array,
      default: () => [],
    },
    disablePurchasing: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-request-object': (payload) => {
      return (
        (typeof payload.response === 'object' || typeof payload.response === 'boolean') &&
        typeof payload.type === 'string'
      )
    },
  },

  data() {
    return {
      klarnaLocale: this.buildKlarnaLocale(),
      currentHandle: cnvs.page.handle,
      namespace: 'MainProduct',
      state: 'loading',
      addons: [],
      widgets: null,
    }
  },

  computed: {
    compareAtPrice() {
      let newPrice =
        this.variant?.compareAtPrice?.amount || this.product?.compareAtPrice?.amount || false

      this.addons.forEach((addon) => {
        if (addon.price.amount) {
          newPrice += addon.price.amount
        }
      })
      return newPrice
    },

    /**
     * Update variant price when selecting addon.
     */
    price() {
      let newPrice = this.variant?.price?.amount

      this.addons.forEach((addon) => {
        if (addon.price.amount) {
          newPrice += addon.price.amount
        }
      })

      this.updateAlmaWidget()

      return newPrice
    },

    /**
     * Compute if Klarna widget should be shown for current product.
     * @returns {Boolean}
     */
    klarnaIsEnabled() {
      if (!this.product) {
        return false
      }

      if (cnvs.store.language === 'fr') {
        return false
      }

      return !this.product.tags.includes('hide_klarna')
    },
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },
  },

  mounted() {
    this.namespace = `MainProduct-${this.containerContext}-${this.handle}`
    this.setProduct()

    this.setupAlmaWidget()
  },

  methods: {
    setupAlmaWidget(firstTime = true) {
      if (cnvs.store.language !== 'fr') {
        return
      }
      if (!window.Alma) {
        if (firstTime) {
          setTimeout(() => {
            this.setupAlmaWidget(false)
          }, 3000)
        }
        return
      }
      this.widgets = window.Alma.Widgets.initialize(
        'merchant_121cF2P644ym1KFQUDArphreeZ0T11Crme',
        window.Alma.ApiMode.TEST,
      )
    },

    updateAlmaWidget() {
      if (this.widgets && this.price && window.Alma) {
        this.widgets.add(window.Alma.Widgets.PaymentPlans, {
          container: '#alma-widget',
          purchaseAmount: this.price,
          locale: 'fr',
          transitionDelay: -1,
          hideIfNotEligible: false,
          suggestedPaymentPlan: 4,
          plans: [
            {
              installmentsCount: 1,
              minAmount: 50,
              maxAmount: 500000,
            },
            {
              installmentsCount: 2,
              minAmount: 5000,
              maxAmount: 500000,
            },
            {
              installmentsCount: 3,
              minAmount: 5000,
              maxAmount: 500000,
            },
            {
              installmentsCount: 4,
              minAmount: 5000,
              maxAmount: 500000,
            },
          ],
        })
      }
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const response = await cnvs.Query({
          query: product,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = variant?.id
        ? `${location.origin}${location.pathname}?variant=${variant.id}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * update addon
     * @param {Object} addon - addon data
     */
    updateAddOn(addon) {
      if (addon.isActive) {
        this.addons.push(addon)
      } else {
        const addonIndex = this.addons.findIndex((item) => {
          return item.id === addon.id
        })

        this.addons.splice(addonIndex, 1)
      }
    },

    /**
     * Clear addons
     */
    clearAddons() {
      this.addons = []
    },

    /**
     * Determine valid Klarna locale based on selected country & language.
     * List can be found here:
     * https://docs.klarna.com/payments/web-payments/before-you-start/data-requirements/puchase-countries-currencies-locales/
     * @returns {String}
     */
    buildKlarnaLocale() {
      const englishLanguageKey = 'en'
      const countriesWithGermanLanguageSupport = [
        'AT',
        'DE',
        'CH',
      ]

      const language = cnvs.store.language
      const country = cnvs.store.country

      const selectedLocale = `${language}-${country}`

      if (countriesWithGermanLanguageSupport.includes(country)) {
        return selectedLocale
      }

      return `${englishLanguageKey}-${country}`
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
